import classNames from 'classnames';
import propTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { Link, useLocation } from 'react-router-dom';
import Avatar from '../Avatar';
import Badge from '../Badge';
import Button from '../Button';
import Image from '../Image';
import { Icon } from '../KeyComponents';
import messages from './messages';

function SideNavbar({
  logo,
  logoTrademark,
  logoDark,
  logoDarkTrademark,
  collapsed: propCollapsed,
  user,
  dashboardMenu,
  dashboardFooter,
  dashboardLink,
  profileMenu,
  profileLink,
  staffDashboardLink,
  children,
  logoLink,
  organizationsLink,
  isDarkMode,
  logoutLink,
  logoutOnClick,
}) {
  const dropdownRef = useRef(null);
  const intl = useIntl();

  const isTablet = useMediaQuery({ maxWidth: 1180 });

  const collapsed = propCollapsed || isTablet;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [logoSrc, setLogoSrc] = useState(collapsed ? (isDarkMode ? logoDark : logo) : (isDarkMode ? logoDarkTrademark : logoTrademark));

  function getRoleVariant(role) {
    switch (role) {
      case 'super_admin':
        return 'success';
      case 'staff':
        return 'purple';
      default:
        return 'primary';
    }
  }

  function getRoleText(role) {
    switch (role) {
      case 'super_admin':
        return intl.formatMessage(messages.admin);
      case 'staff':
        return intl.formatMessage(messages.staff);
      default:
        return intl.formatMessage(messages.learner);
    }
  }

  // Update logoSrc when dark mode or collapsed state changes
  useEffect(() => {
    setLogoSrc(collapsed ? (isDarkMode ? logoDark : logo) : (isDarkMode ? logoDarkTrademark : logoTrademark));
  }, [isDarkMode, collapsed, logo, logoTrademark, logoDark, logoDarkTrademark]);

  useEffect(() => {
    const sideNavbar = document.getElementsByClassName('side-navbar')[0];

    const handleMouseEnter = () => {
      if (collapsed) {
        setLogoSrc(isDarkMode ? logoDarkTrademark : logoTrademark);
      }
    };

    const handleMouseLeave = () => {
      if (collapsed) {
        setLogoSrc(isDarkMode ? logoDark : logo);
        setIsDropdownOpen(false);
      }
    };

    sideNavbar.addEventListener('mouseenter', handleMouseEnter);
    sideNavbar.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      sideNavbar.removeEventListener('mouseenter', handleMouseEnter);
      sideNavbar.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [collapsed, isDarkMode, logo, logoTrademark, logoDark, logoDarkTrademark]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }

    // Only add the click listener when dropdown is open
    if (isDropdownOpen) {
      // Use capture phase to ensure this handler runs before other click handlers
      document.addEventListener('mousedown', handleClickOutside, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isDropdownOpen]);

  return (
    <SideNavbarContainer>
      <div className={classNames('side-navbar', { collapsed })}>
        <div className="side-navbar__head">
          <Link to={logoLink} className="side-navbar__logo" role="link" tabIndex={0}>
            <Image
              src={logoSrc}
              alt="logo"
              className="side-navbar__logo-image"
            />
          </Link>
          <div className="side-navbar__user-dropdown" ref={dropdownRef}>
            <div className="side-navbar__user-dropdown-toggle-container">
              <button
                className="side-navbar__user-dropdown-toggle toggle-main"
                id="dropdown-basic"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <Avatar type="image" size="xs" src={user.image} variant="primary" alt={user.name} />
                <div className="side-navbar__user-dropdown-toggle__head">
                  <div className="side-navbar__user-dropdown-toggle__head__name truncate">
                    {user.name ? user.name.split(' ')[0] : user.username ? user.username : user.email.split('@')[0]}
                  </div>
                  <Badge variant={getRoleVariant(user.role.toLowerCase())} size="sm">{getRoleText(user.role.toLowerCase())}</Badge>
                </div>
                <Icon icon={isDropdownOpen ? "arrow-up-s" : "arrow-down-s"} type="line" className="side-navbar__user-dropdown-toggle__icon" />
              </button>
              {staffDashboardLink &&
                <Button
                  iconBefore="logout-box"
                  iconType="line"
                  variant="outline-gray"
                  size="sm"
                  className="side-navbar__user-dropdown-redirect__button"
                  href={staffDashboardLink}
                  role="link"
                  tabIndex={0}
                >
                  {intl.formatMessage(messages.exitLearnerView)}
                </Button>
              }
            </div>
            {isDropdownOpen && (
              <div className="side-navbar__user-dropdown-menu">
                <button
                  id="dropdown-basic"
                  className="side-navbar__user-dropdown-toggle"
                  variant="transparent"
                  onClick={() => {
                    setIsDropdownOpen(!isDropdownOpen);
                    setLogoSrc(collapsed ? logo : logoTrademark);
                  }}
                >
                  <Avatar type="image" size="xs" src={user.image} variant="primary" alt={user.name} />
                  <div className="side-navbar__user-dropdown-toggle__head">
                    <div className="side-navbar__user-dropdown-toggle__head__name">
                      {user.name ? user.name : user.username ? user.username : user.email.split('@')[0]}
                    </div>
                    <div className="side-navbar__user-dropdown-toggle__head__email">
                      {user.email}
                    </div>
                  </div>
                  <Icon icon="arrow-up-s" type="line" className="side-navbar__user-dropdown-toggle__icon" />
                </button>
                <div className="side-navbar__user-dropdown-menu__profile">
                  <Button
                    variant="outline-gray"
                    size="sm"
                    className={classNames("side-navbar__user-dropdown-menu__button", { "side-navbar__user-dropdown-menu__button--switch": dashboardLink })}
                    href={profileLink}
                    role="link"
                    tabIndex={0}
                  >
                    {intl.formatMessage(messages.viewProfile)}
                  </Button>
                  {dashboardLink && <Button
                    variant="outline-gray"
                    size="sm"
                    className={classNames("side-navbar__user-dropdown-menu__button", { "side-navbar__user-dropdown-menu__button--switch": dashboardLink })}
                    href={dashboardLink}
                    role="link"
                    tabIndex={0}
                  >
                    {intl.formatMessage(messages.switchToLearner)}
                    <Icon icon="arrow-right-up" type="line" size='sm' />
                  </Button>}
                  {organizationsLink && <Button
                    variant="outline-gray"
                    size="sm"
                    className={classNames("side-navbar__user-dropdown-menu__button", { "side-navbar__user-dropdown-menu__button--switch": dashboardLink })}
                    href={organizationsLink}
                  >
                    {intl.formatMessage(messages.organizations)}
                    <Icon icon="building" type="line" size='sm' />
                  </Button>}
                </div>
                {profileMenu.map((menu, index) => {
                  return (
                    <Link
                      key={index}
                      to={menu.href}
                      className="side-navbar__user-dropdown-menu__item"
                      role="link"
                      tabIndex={0}
                    >
                      {menu.text}
                    </Link>
                  );
                })}
                {logoutLink && (
                  <Link
                    to={logoutLink}
                    className="side-navbar__user-dropdown-menu__item side-navbar__user-dropdown-menu__item--logout"
                    role="link"
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      logoutOnClick();
                    }}
                  >
                    <Icon icon="logout-circle" type="line" size='xs' />
                    {intl.formatMessage(messages.logout)}
                  </Link>
                )}
              </div>
            )}
          </div>
          <div className={classNames("side-navbar__menu", { "side-navbar__menu--is-staff": staffDashboardLink })}>
            {dashboardMenu.filter(menu => !menu.hidden).map((menu, index) => {
              if (menu.children) {
                return <SideNavbarItem index={index} menu={menu} propCollapsed={propCollapsed} />;
              }
              const isActive = window.location.href === menu.href;
              return (
                <SideNavbarMenuLink key={index} menu={menu} isActive={isActive} />
              );
            })}
          </div>
        </div>
        <div className="side-navbar__footer">
          {dashboardFooter.map((footer, index) => {
            const isActive = window.location.href === footer.href;
            return (
              <SideNavbarMenuLink key={index} menu={footer} isActive={isActive} />
            );
          })}
        </div>
      </div>
      <div className="side-navbar-content">
        {children}
      </div>
    </SideNavbarContainer>
  );
}

SideNavbar.propTypes = {
  logo: propTypes.string,
  logoTrademark: propTypes.string,
  logoDark: propTypes.string,
  logoDarkTrademark: propTypes.string,
  collapsed: propTypes.bool,
  isDarkMode: propTypes.bool,
  user: propTypes.shape({
    name: propTypes.string,
    username: propTypes.string,
    email: propTypes.string,
    role: propTypes.string,
    image: propTypes.string,
  }).isRequired,
  dashboardMenu: propTypes.arrayOf(propTypes.shape({
    label: propTypes.string,
    icon: propTypes.string,
    iconType: propTypes.string,
    link: propTypes.string,
    children: propTypes.arrayOf(propTypes.shape({
      label: propTypes.string,
      link: propTypes.string,
    })),
  })).isRequired,
  dashboardFooter: propTypes.arrayOf(propTypes.shape({
    label: propTypes.string,
    icon: propTypes.string,
    iconType: propTypes.string,
    link: propTypes.string,
  })).isRequired,
  profileMenu: propTypes.arrayOf(propTypes.shape({
    label: propTypes.string,
    icon: propTypes.string,
    iconType: propTypes.string,
    link: propTypes.string,
  })).isRequired,
  profileLink: propTypes.string.isRequired,
  staffDashboardLink: propTypes.string,
  children: propTypes.node,
  logoLink: propTypes.string.isRequired,
  organizationsLink: propTypes.string,
  logoutLink: propTypes.string,
};

SideNavbar.defaultProps = {
  logo: '',
  logoTrademark: '',
  logoDark: '',
  logoDarkTrademark: '',
  collapsed: false,
  isDarkMode: false,
  staffDashboardLink: null,
  children: null,
  organizationsLink: null,
  logoutLink: null,
};

function SideNavbarMenuLink({ menu, isActive }) {
  const [isBlinking, setIsBlinking] = useState(false);

  useEffect(() => {
    if (menu.iconSecondary) {
      const interval = setInterval(() => {
        setIsBlinking(prev => !prev);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [menu.iconSecondary]);

  return (
    <Link to={menu.href} className={classNames('side-navbar__menu-item', { active: isActive })} role="link" tabIndex={0}>
      <div className="side-navbar__menu-item__head">
        <Icon
          icon={isBlinking ? menu.iconSecondary : menu.icon}
          type={menu.iconType === '' ? menu.iconType : 'line'}
          className={classNames(
            'side-navbar__menu-item__icon',
            { 'icon-rotate': menu.iconSecondary }
          )}
        />
        <span className="side-navbar__menu-item__text">{menu.text}</span>
      </div>
    </Link>
  );
}

SideNavbarMenuLink.propTypes = {
  menu: propTypes.object,
  isActive: propTypes.bool,
};

SideNavbarMenuLink.defaultProps = {
  isActive: false,
};

function SideNavbarItem({ menu, index, propCollapsed }) {
  const [show, setShow] = useState(false);
  const [isBlinking, setIsBlinking] = useState(false);
  const isActive = menu.children.some(child => window.location.href === child.href);
  const isTablet = useMediaQuery({ maxWidth: 1180 });
  const collapsed = propCollapsed || isTablet;

  useEffect(() => {
    if (menu.iconSecondary) {
      const interval = setInterval(() => {
        setIsBlinking(prev => !prev);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [menu.iconSecondary]);

  useEffect(() => {
    const sideNavbar = document.getElementsByClassName('side-navbar')[0];

    const handleMouseLeave = () => {
      if (collapsed) {
        setShow(false);
      }
    };

    sideNavbar.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      sideNavbar.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [collapsed]);

  return (
    <div className={classNames('side-navbar__menu-dropdown', { active: isActive || show })} key={index}>
      <div className={classNames('side-navbar__menu-item side-navbar__menu-dropdown-toggle', { active: show })} onClick={() => setShow(!show)} onKeyPress={() => setShow(!show)} role="link" tabIndex={0}>
        <div className="side-navbar__menu-item__head">
          <Icon
            icon={isBlinking ? menu.iconSecondary : menu.icon}
            type={menu.iconType === '' ? menu.iconType : 'line'}
            className={classNames(
              'side-navbar__menu-item__icon',
              { 'icon-rotate': menu.iconSecondary }
            )}
          />
          <span className="side-navbar__menu-item__text">{menu.text}</span>
        </div>
        <Icon icon="arrow-down-s" type="line" className="side-navbar__menu-dropdown-toggle__icon" />
      </div>
      <div className={classNames('side-navbar__menu-dropdown-menu', { show })}>
        {menu.children.map((child, itemIndex) => {
          const isItemActive = window.location.href?.includes(child.href);
          return (
            <Link key={itemIndex} to={child.href} className={classNames('side-navbar__menu-dropdown-menu__item', { active: isItemActive })} role="link" tabIndex={0}>
              <div className="side-navbar__menu-dropdown-menu__item__line" />
              <div className="side-navbar__menu-dropdown-menu__item__head">
                <div className="side-navbar__menu-dropdown-menu__item__text">
                  {child.text}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

SideNavbarItem.propTypes = {
  children: propTypes.node,
  menu: propTypes.object,
  index: propTypes.number,
  propCollapsed: propTypes.bool,
};

SideNavbarItem.defaultProps = {
  children: null,
  menu: {},
  index: 0,
  propCollapsed: false,
};

function SideNavbarContainer({ children }) {
  return (
    <div className="side-navbar-container">
      <div className="side-navbar-container__overlay" />
      {children}
    </div>
  );
}

SideNavbarContainer.propTypes = {
  children: propTypes.node,
};

SideNavbarContainer.defaultProps = {
  children: null,
};

export default SideNavbar;
